import { Section, H2 } from "./common-components";
import { capitalizeFirstLetter } from "../../../helpers/functions";

function getVaccineDisplayName(row) {
  const n = row?.name;
  if (!n) return "";
  if (typeof n === "string") return capitalizeFirstLetter(n);
  if (typeof n !== "object") return "";
  const paths = [n?.name, n?.value, n?.label, n?.name?.name, n?.name?.value, n?.name?.label];
  const s = paths.find((p) => typeof p === "string" && p) ?? Object.values(n).find((v) => typeof v === "string" && v);
  return s ? capitalizeFirstLetter(s) : "";
}

function getDisplayValue(row, keys = []) {
  for (const key of keys) {
    const value = row?.[key];
    if (value === null || value === undefined || value === "") continue;

    if (typeof value === "string" || typeof value === "number") {
      return String(value);
    }

    if (typeof value === "object") {
      const nested =
        value?.label ??
        value?.value ??
        value?.name ??
        value?.doseType ??
        value?.totalDoses ??
        value?.totalNumberOfVaccineDoses;
      if (nested !== null && nested !== undefined && nested !== "") {
        return String(nested);
      }
    }
  }
  return "";
}

function normalizeNameForMatch(value) {
  return String(value || "").trim().toLowerCase();
}

export function Vaccines({
  formType,
  education,
  vaccines: vaccinesProp,
  patientMedications = [],
}) {
  const rows = Array.isArray(vaccinesProp ?? education?.vaccineSchedule)
    ? (vaccinesProp ?? education?.vaccineSchedule)
    : [];
  const vaccineMedications = Array.isArray(patientMedications)
    ? patientMedications.filter(
        (med) => normalizeNameForMatch(med?.medicationType) === "vaccine"
      )
    : [];
  const medicationByName = new Map(
    vaccineMedications.map((med) => [
      normalizeNameForMatch(
        med?.medicationName ?? med?.name ?? med?.displayName
      ),
      med,
    ])
  );
  const profileDrivenRows = rows.filter((row) =>
    medicationByName.has(normalizeNameForMatch(getVaccineDisplayName(row)))
  );
  const fallbackRowsFromChart = vaccineMedications.map((med) => ({
    name: med?.medicationName ?? med?.name ?? med?.displayName ?? "",
    vaccineDoseType: med?.vaccineDoseType ?? med?.vaccineDose ?? med?.doseType ?? "",
    totalNumberOfVaccineDoses:
      med?.totalNumberOfVaccineDoses ??
      med?.totalNumberOfDoses ??
      med?.vaccineDoses ??
      med?.totalDoses ??
      med?.doses ??
      "",
    reason: med?.reason ?? med?.vaccineReason ?? med?.prnReason ?? "",
  }));
  const rowsToRender =
    profileDrivenRows.length > 0 ? profileDrivenRows : fallbackRowsFromChart;
  const title = rowsToRender.length > 0 ? "Vaccines" : "Vaccines - No Information";

  return (
    <Section formType={formType} className="space-y-2">
      <H2>{title}</H2>
      {rowsToRender.length > 0 && (
        <table className="text-xs border border-gray-400 border-collapse w-full rounded-md table-auto">
          <thead>
            <tr className="text-left bg-gray-20">
              <th className="border font-normal border-gray-400 p-1 w-[38%]">Vaccine Name</th>
              <th className="border font-normal border-gray-400 p-1 w-[20%]">Vaccine Dose Type</th>
              <th className="border font-normal border-gray-400 p-1 w-[17%]">Vaccine Doses</th>
              <th className="border font-normal border-gray-400 p-1 w-[25%]">Reason</th>
            </tr>
          </thead>
          <tbody>
            {rowsToRender.map((row, idx) => {
              const rowName = getVaccineDisplayName(row);
              const matchingMedication = medicationByName.get(
                normalizeNameForMatch(rowName)
              );

              return (
              <tr className="font-semibold" key={`vaccine-${idx}-${rowName}`}>
                <td className="border border-gray-400 p-1 w-[38%]">
                  {rowName}
                </td>
                <td className="border border-gray-400 p-1 w-[20%]">
                  {getDisplayValue(row, ["vaccineDoseType", "vaccineDose", "doseType"]) ||
                    getDisplayValue(matchingMedication, [
                      "vaccineDoseType",
                      "vaccineDose",
                      "doseType",
                    ])}
                </td>
                <td className="border border-gray-400 p-1 w-[17%]">
                  {getDisplayValue(row, [
                    "totalNumberOfVaccineDoses",
                    "totalNumberOfDoses",
                    "vaccineDoses",
                    "totalDoses",
                    "doses",
                  ]) ||
                    getDisplayValue(matchingMedication, [
                      "totalNumberOfVaccineDoses",
                      "totalNumberOfDoses",
                      "vaccineDoses",
                      "totalDoses",
                      "doses",
                    ])}
                </td>
                <td className="border border-gray-400 p-1 w-[25%]">
                  {getDisplayValue(row, ["reason", "vaccineReason", "prnReason"]) ||
                    getDisplayValue(matchingMedication, [
                      "reason",
                      "vaccineReason",
                      "prnReason",
                    ])}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </Section>
  );
}
